import React, { forwardRef, useContext, ComponentPropsWithRef } from "react";
import PropTypes from "prop-types";
import cn from "classnames";

import {
  ReactComponentStaticProps,
  ReactDefaultPropsWithChildren,
} from "../../types/react";

import { ThemeContext } from "../Theme";
import { useUniqueId } from "../hooks/useUniqueId";
import { mapChildrenIndexedByType } from "../../helper/map-children";

import { AccordionProvider } from "./AccordionContext";
import { AccordionItem } from "./AccordionItem";
import { AccordionPanel } from "./AccordionPanel";

import styles from "./Accordion.scss";

type AccordionBaseProps = {
  customClass?: string | string[];
};
export type AccordionProps = AccordionBaseProps &
  Omit<ComponentPropsWithRef<"div">, keyof AccordionBaseProps>;

const defaultProps = {
  customClass: "",
  children: "",
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  AccordionBaseProps,
  typeof defaultProps
> = defaultProps;

const AccordionWithRef = (
  props: AccordionProps,
  ref: ComponentPropsWithRef<"div">["ref"]
) => {
  const { customClass, children, ...restProps } = props;
  const { id } = restProps;
  const accordionId = id || useUniqueId("Accordion", props) + 1;

  const theme = useContext(ThemeContext);

  return (
    <div
      ref={ref}
      className={cn(styles.accordion, theme && styles[theme], customClass)}
      data-accordion-component="Accordion"
      {...restProps}
    >
      {mapChildrenIndexedByType(children, (child, itemId) => (
        <AccordionProvider value={{ id: accordionId, itemId: itemId + 1 }}>
          {child}
        </AccordionProvider>
      ))}
    </div>
  );
};

export const Accordion = (forwardRef(AccordionWithRef) as unknown) as ((
  props: AccordionProps
) => JSX.Element) & {
  Item: typeof AccordionItem;
  Panel: typeof AccordionPanel;
} & ReactComponentStaticProps<AccordionProps>;

Accordion.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  customClass: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.arrayOf(PropTypes.string.isRequired),
  ]),
};

Accordion.defaultProps = validatedDefaultProps;

Accordion.Item = AccordionItem;
Accordion.Panel = AccordionPanel;

Accordion.displayName = "Accordion";

export default Accordion;
